import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import {
  formatRefreshOutcomeTally,
  outcomeBadgeLabel,
  RefreshJobNotice,
  RefreshResultsToastService,
} from './refresh-results-toast.service';
import { BriefRefreshPostOutcome } from '@/app/services/super-social/super-social.service';

@Component({
  selector: 'atom-refresh-results-toast',
  templateUrl: './refresh-results-toast.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RefreshResultsToast {
  protected readonly operations = inject(RefreshResultsToastService);
  private readonly now = signal(Date.now());

  protected readonly hasNotices = computed(() => this.operations.notices().length > 0);
  protected readonly runningCount = computed(
    () => this.operations.notices().filter((notice) => notice.status === 'running').length,
  );
  protected readonly headerSummary = computed(() => {
    const total = this.operations.notices().length;
    const running = this.runningCount();
    if (running > 0) {
      return running === 1
        ? '1 results refresh in progress'
        : `${running} results refreshes in progress`;
    }
    return total === 1 ? '1 recent operation' : `${total} recent operations`;
  });

  protected readonly chevronClass = computed(() =>
    this.operations.panelExpanded()
      ? 'size-5 shrink-0 transition-transform rotate-0'
      : 'size-5 shrink-0 transition-transform rotate-180',
  );

  constructor() {
    effect((onCleanup) => {
      const needsClock = this.operations.notices().some((notice) => notice.status === 'running');
      if (!needsClock) {
        return;
      }
      this.now.set(Date.now());
      const timer = setInterval(() => this.now.set(Date.now()), 1000);
      onCleanup(() => clearInterval(timer));
    });
  }

  toggleExpanded(): void {
    this.operations.togglePanelExpanded();
  }

  statusLabel(status: RefreshJobNotice['status']): string {
    if (status === 'complete') {
      return 'Last refresh';
    }
    if (status === 'error') {
      return 'Refresh failed';
    }
    return 'Refreshing results';
  }

  tallyLabel(notice: RefreshJobNotice): string {
    return formatRefreshOutcomeTally(notice.counts, notice.status);
  }

  platformLabel(outcome: BriefRefreshPostOutcome): string {
    return outcome.platform === 'tiktok' ? 'TikTok' : 'Instagram';
  }

  badgeLabel(outcome: BriefRefreshPostOutcome, notice: RefreshJobNotice): string {
    return outcomeBadgeLabel(outcome.status, notice.status);
  }

  badgeClass(outcome: BriefRefreshPostOutcome): string {
    if (outcome.status === 'queued') {
      return 'bg-emerald-500/20 text-emerald-200';
    }
    if (outcome.status === 'schedule_error') {
      return 'bg-red-500/20 text-red-200';
    }
    return 'bg-amber-500/20 text-amber-100';
  }

  elapsedLabel(notice: RefreshJobNotice): string {
    const endMs =
      notice.status === 'running'
        ? this.now()
        : (notice.completedAt ?? this.now());
    const elapsedMs = Math.max(0, endMs - new Date(notice.triggeredAt).getTime());
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes} min ${seconds} sec`;
  }

  rowStatusClass(status: RefreshJobNotice['status']): string {
    if (status === 'complete') {
      return 'text-emerald-300';
    }
    if (status === 'error') {
      return 'text-red-300';
    }
    return 'text-sky-300';
  }
}
